import { EventType, Program } from '@citruscamps/citrus-client'
import React, { Fragment } from 'react'
import { Link } from 'react-router-dom'
import { ROUTES } from '../../constants/routes'
import { NoteRow } from '../../pages/Contacts/pages/ContactDetails/components/NoteRow'
import { formatFullName, formatList, formatRefundReason } from '../../utils/formatters'
import { DefaultActivityText } from './DefaultActivityText'
import { ActivityEvent } from './types/ActivityEvent'

export interface IProps {
  activity: ActivityEvent
  program: Pick<Program, 'id' | 'name'>
}

export const ActivityText = ({ activity, program }: IProps) => {
  const [item, action] = activity.type.split('.').map((i) => i.replace('_', ' '))
  const isOwnAccount: boolean =
    'user_id' in activity.data && activity.user?.id === activity.data.user_id
  switch (activity.type) {
    case EventType.program_attendee_created:
    case EventType.program_attendee_updated:
    case EventType.program_attendee_bulk_created:
    case EventType.program_attendee_viewed_health_card:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'}{' '}
          {activity.type.includes('bulk_created')
            ? 'bulk added'
            : activity.type.includes('created')
            ? 'added'
            : activity.type.includes('viewed_health_card')
            ? 'viewed the health card number for '
            : action}{' '}
          <Link to={ROUTES.ATTENDEE_DETAILS.replace(':id', activity.data.id)}>
            {formatFullName(activity.data) || 'unknown attendee'}
          </Link>
          .
        </>
      )
    case EventType.program_attendee_disconnected:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} removed{' '}
          {formatFullName(activity.data) || 'unknown attendee'}.
        </>
      )
    case EventType.program_attendee_merged:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} merged{' '}
          {formatFullName(activity.data.merge_from) || 'unknown attendee'} into{' '}
          <Link to={ROUTES.ATTENDEE_DETAILS.replace(':id', activity.data.merge_into?.id || '')}>
            {formatFullName(activity.data.merge_into) || 'unknown attendee'}
          </Link>
        </>
      )
    case EventType.campaign_created:
    case EventType.campaign_updated:
    case EventType.campaign_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} campaign{' '}
          {activity.data.subject}
          {activity.data.segment_options?.conditions?.map((c, i) => {
            if (c.field === 'event_id') {
              return (
                <Fragment key={[c.field, c.op, c.value].join(':')}>
                  {' '}
                  for{' '}
                  <Link to={ROUTES.EVENT_EDIT_DASHBOARD.replace(':id', c.value || '')}>event</Link>.
                </Fragment>
              )
            }
            return '.'
          })}
        </>
      )
    case EventType.conversation_updated:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} updated{' '}
          <Link to={ROUTES.INBOX_CONVERSATION_DETAILS.replace(':id', activity.data.id)}>
            {activity.data.subject}
          </Link>{' '}
          conversation.
        </>
      )
    case EventType.conversation_mark_open:
    case EventType.conversation_bulk_mark_open:
    case EventType.conversation_bulk_updated:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} moved{' '}
          <Link
            to={ROUTES.INBOX_CONVERSATION_DETAILS.replace(':id', activity.data.id).concat(
              '?type=to_do',
            )}
          >
            {activity.data.subject}
          </Link>{' '}
          conversation to <Link to={ROUTES.INBOX.concat('?type=to_do')}>To Do</Link>.
        </>
      )
    case EventType.conversation_mark_closed:
    case EventType.conversation_bulk_mark_closed:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} moved{' '}
          <Link
            to={ROUTES.INBOX_CONVERSATION_DETAILS.replace(':id', activity.data.id).concat(
              '?type=done',
            )}
          >
            {activity.data.subject}
          </Link>{' '}
          conversation to <Link to={ROUTES.INBOX.concat('?type=done')}>Done</Link>.
        </>
      )
    case EventType.conversation_archive:
    case EventType.conversation_bulk_archive:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} moved{' '}
          <Link
            to={ROUTES.INBOX_CONVERSATION_DETAILS.replace(':id', activity.data.id).concat(
              '?type=trash',
            )}
          >
            {activity.data.subject}
          </Link>{' '}
          conversation to <Link to={ROUTES.INBOX.concat('?type=trash')}>Trash</Link>.
        </>
      )
    case EventType.message_created:
    case EventType.message_sent:
    case EventType.message_bulk_created:
      return activity.data.type === 'outbound_contact' ? (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'}{' '}
          {action.includes('sent') ? 'sent' : action} message{' '}
          <Link
            to={ROUTES.INBOX_CONVERSATION_DETAILS.replace(
              ':id',
              activity.data.conversation_id || '',
            )}
          >
            {activity.data.subject}
          </Link>{' '}
          {action === 'created' ? 'for' : 'to'}{' '}
          {activity.data.contact ? (
            <Link to={ROUTES.CONTACT_DETAILS.replace(':id', activity.data.contact?.id || '')}>
              {!!activity.user?.id && activity.user?.id === activity.data.contact?.user_id
                ? 'themselves'
                : formatFullName(activity.data.contact)}
            </Link>
          ) : (
            'Unknown contact'
          )}
          .
        </>
      ) : (
        <>
          {program?.name || 'Citrus'}
          {action === 'created' ? ` ${action} inbound` : ' received'} message{' '}
          <Link
            to={ROUTES.INBOX_CONVERSATION_DETAILS.replace(
              ':id',
              activity.data.conversation_id || '',
            )}
          >
            {activity.data.subject}
          </Link>{' '}
          from{' '}
          {activity.data.contact ? (
            <Link to={ROUTES.CONTACT_DETAILS.replace(':id', activity.data.contact?.id || '')}>
              {formatFullName(activity.data.contact)}
            </Link>
          ) : (
            'Unknown contact'
          )}
          .
        </>
      )
    case EventType.coupon_created:
    case EventType.coupon_updated:
    case EventType.coupon_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} coupon{' '}
          <Link to={ROUTES.FINANCIALS_COUPONS}>{activity.data?.code || 'Unknown code'}</Link>.
        </>
      )
    case EventType.discount_created:
    case EventType.discount_updated:
    case EventType.discount_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
          <Link to={ROUTES.FINANCIALS_COUPONS}>
            {activity.data?.coupon?.code || 'Unknown code'}
          </Link>{' '}
          discount for{' '}
          <Link to={ROUTES.FINANCIALS_ORDER_DETAIL.replace(':id', activity.data?.order_id)}>
            order
          </Link>
          .
        </>
      )
    case EventType.contact_created:
    case EventType.contact_updated:
    case EventType.contact_subscribed:
    case EventType.contact_unsubscribed:
    case EventType.contact_bulk_created:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'}{' '}
          {activity.type.includes('bulk_created')
            ? 'bulk added'
            : activity.type.includes('created')
            ? 'added'
            : action}{' '}
          <Link to={ROUTES.CONTACT_DETAILS.replace(':id', activity.data.id)}>
            {formatFullName(activity.data) || 'unknown contact'}
          </Link>
          .
        </>
      )
    case EventType.contact_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} removed{' '}
          {formatFullName(activity.data) || 'unknown contact'}.
        </>
      )
    case EventType.contact_account_balance_created:
      return (
        <div>
          <div>
            {activity.user ? activity.user.first_name : 'Citrus'} adjusted balance{' '}
            <Link to={ROUTES.CONTACT_TRANSACTIONS.replace(':id', activity.data.contact_id)}>
              {activity.data.amount.display} for contact
            </Link>
            .
          </div>
          <div className="small text-body-secondary">{activity.data.memo}</div>
        </div>
      )
    case EventType.contact_account_balance_updated:
      return (
        <div>
          <div>
            {activity.user ? activity.user.first_name : 'Citrus'} updated adjust note for balance{' '}
            <Link to={ROUTES.CONTACT_TRANSACTIONS.replace(':id', activity.data.contact_id)}>
              {activity.data.amount.display} for contact
            </Link>
            .
          </div>
          <div className="small text-body-secondary">{activity.data.memo}</div>
        </div>
      )
    case EventType.feedback_bulk_created:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'}{' '}
          {activity.type.includes('event') ? `bulk created event` : action} feedback for{' '}
          <Link to={ROUTES.ATTENDEE_DETAILS.replace(':id', activity.data.program_attendee.id)}>
            {activity.data.program_attendee
              ? formatFullName(activity.data.program_attendee)
              : 'Unknown attendee'}
          </Link>
          .
        </>
      )
    case EventType.feedback_updated:
      const firstName = activity.user ? activity.user.first_name : activity.data.contact.first_name
      return (
        <>
          <div>
            {firstName} gave feedback saying{' '}
            <Link to={ROUTES.ATTENDEE_DETAILS.replace(':id', activity.data.program_attendee.id)}>
              {activity.data.program_attendee
                ? formatFullName(activity.data.program_attendee)
                : 'Unknown attendee'}
            </Link>{' '}
            had a {activity.data.rating || 'unknown rating'} experience.
          </div>
          {activity.data.comments ? (
            <div className="small text-body-secondary py-1">"{activity.data.comments}"</div>
          ) : null}
        </>
      )
    case EventType.feedback_created:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} feedback for{' '}
          <Link to={ROUTES.ATTENDEE_DETAILS.replace(':id', activity.data.program_attendee.id)}>
            {activity.data.program_attendee
              ? formatFullName(activity.data.program_attendee)
              : 'Unknown attendee'}
          </Link>
          .
        </>
      )
    case EventType.invite_created:
    case EventType.invite_accepted:
    case EventType.invite_deleted:
    case EventType.invite_bulk_created:
      return isOwnAccount ? (
        <>
          <Link to={ROUTES.TEAM}>
            {formatFullName(activity.data) || formatFullName(activity.user) || 'Unknown user'}
          </Link>{' '}
          {action} their invite.
        </>
      ) : (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
          <Link to={ROUTES.TEAM}>{formatFullName(activity.data) || 'team member'}</Link>
          's invite.
        </>
      )
    case EventType.member_created:
    case EventType.member_updated:
    case EventType.member_deleted:
      return isOwnAccount ? (
        <>
          <Link to={ROUTES.TEAM}>
            {formatFullName(activity.data) || formatFullName(activity.user) || 'Unknown user'}
          </Link>{' '}
          {action} their account.
        </>
      ) : (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
          <Link to={ROUTES.TEAM}>{formatFullName(activity.data) || 'team member'}</Link>
          's account.
        </>
      )
    case EventType.program_created:
    case EventType.program_updated:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
          <Link to={ROUTES.PROGRAM_SETTINGS}>{activity.data.name || 'program'}</Link>.
        </>
      )
    case EventType.order_created:
    case EventType.order_updated:
    case EventType.order_processed:
    case EventType.order_refunded:
    case EventType.order_bulk_created:
    case EventType.order_bulk_updated:
    case EventType.order_bulk_processed:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} order{' '}
          {activity.data.receipt_number ? (
            <Link to={ROUTES.FINANCIALS_ORDER_DETAIL.replace(':id', activity.data.id)}>
              {'#' + activity.data.receipt_number}
            </Link>
          ) : (
            'order'
          )}
          .
        </>
      )
    case EventType.order_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} order{' '}
          {'#' + activity.data.receipt_number || 'order'}.
        </>
      )
    case EventType.refund_created:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'}{' '}
          {activity.data.order_id ? (
            <Link to={ROUTES.FINANCIALS_ORDER_DETAIL.replace(':id', activity.data.order_id)}>
              refunded order
            </Link>
          ) : (
            `refunded order`
          )}{' '}
          {activity.data.amount.display} due to{' '}
          {formatRefundReason(activity.data.reason, true).toLowerCase()}.
        </>
      )
    case EventType.refund_updated:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} the refund for{' '}
          {activity.data.order_id ? (
            <Link to={ROUTES.FINANCIALS_ORDER_DETAIL.replace(':id', activity.data.order_id)}>
              order
            </Link>
          ) : (
            'order'
          )}
          .
        </>
      )
    case EventType.order_dispute_funds_reinstated:
    case EventType.order_dispute_funds_withdrawn:
      const disputeFundsAction = activity.type.split('.')?.pop()?.split('_')?.pop()
      return (
        <>
          Funds have been {disputeFundsAction || 'changed'} in regards to the dispute for order{' '}
          <Link to={ROUTES.FINANCIALS_ORDER_DETAIL.replace(':id', activity.data.id)}>
            {'#' + activity.data.receipt_number}
          </Link>
          .
        </>
      )
    case EventType.order_dispute_created:
      const disputes = activity.data.disputes
      return (
        <>
          {'contact' in activity.data
            ? activity.data.contact?.first_name
            : activity.data.contact_name || 'The cardholder'}{' '}
          created dispute for order{' '}
          <Link to={ROUTES.FINANCIALS_ORDER_DETAIL.replace(':id', activity.data.id)}>
            {'#' + activity.data.receipt_number}
          </Link>{' '}
          due to{' '}
          {disputes
            ? formatList(
                disputes.map((dispute) => dispute?.reason.split('_').join(' ')).filter(Boolean),
              )
            : 'unknown reason'}
          .
        </>
      )
    case EventType.order_dispute_updated:
    case EventType.order_dispute_closed:
      const disputeAction = activity.type?.split('.')?.pop()?.replace('_', ' ')
      return (
        <>
          The dispute for order{' '}
          <Link to={ROUTES.FINANCIALS_ORDER_DETAIL.replace(':id', activity.data.id)}>
            {'#' + activity.data.receipt_number}
          </Link>{' '}
          has been {disputeAction || 'changed'}.
        </>
      )
    case EventType.program_event_bulk_published:
    case EventType.program_event_bulk_updated:
    case EventType.program_event_bulk_cancelled:
    case EventType.program_event_bulk_created:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} {activity.data.type}{' '}
          <Link to={ROUTES.EVENT_EDIT_DASHBOARD.replace(':id', activity.data.id)}>
            {activity.data.name || `Unknown ${activity.data.type}`}
          </Link>
          .
        </>
      )
    case EventType.registration_option_created:
    case EventType.registration_option_updated:
    case EventType.registration_option_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} option{' '}
          <Link to={ROUTES.EVENT_EDIT_OPTIONS.replace(':id', activity.data.event_id)}>
            {activity.data.name}
          </Link>{' '}
          for event.
        </>
      )
    case EventType.program_event_updated:
    case EventType.program_event_cancelled:
    case EventType.program_event_created:
    case EventType.program_event_published:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} {activity.data.type}{' '}
          <Link to={ROUTES.EVENT_EDIT_DASHBOARD.replace(':id', activity.data.id)}>
            {activity.data.name || `Unknown ${activity.data.type}`}
          </Link>
          .
        </>
      )
    case EventType.program_event_deleted:
    case EventType.program_event_bulk_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} {activity.data.type}{' '}
          {activity.data.name || `Unknown ${activity.data.type}`}.
        </>
      )
    case EventType.appointment_type_created:
    case EventType.appointment_type_updated:
    case EventType.appointment_type_published:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} appointment type{' '}
          <Link to={ROUTES.APPOINTMENT_EDIT_DASHBOARD.replace(':id', activity.data.id)}>
            {activity.data.name || `Unknown ${activity.data.type}`}
          </Link>
          .
        </>
      )
    case EventType.appointment_type_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} appointment type{' '}
          {activity.data.name || `Unknown ${activity.data.type}`}.
        </>
      )
    case EventType.appointment_created:
    case EventType.appointment_updated:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} scheduled{' '}
          {formatFullName(activity.data.program_attendee) || 'unknown attendee'}'s appointment for{' '}
          <Link to={ROUTES.EVENT_EDIT_DASHBOARD.replace(':id', activity.data.event.id)}>
            {activity.data.event.name || `Unknown ${activity.data.event.type}`}
          </Link>
          .
        </>
      )
    case EventType.registration_created:
    case EventType.registration_bulk_created:
    case EventType.registration_updated:
    case EventType.registration_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
          {formatFullName(activity.data.program_attendee) || 'unknown attendee'}'s registration for{' '}
          <Link to={ROUTES.EVENT_EDIT_DASHBOARD.replace(':id', activity.data.event.id)}>
            {activity.data.event.name || `Unknown ${activity.data.event.type}`}
          </Link>
          .
        </>
      )
    case EventType.tax_rates_created:
    case EventType.tax_rates_updated:
    case EventType.tax_rates_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} tax rate{' '}
          <Link to={ROUTES.FINANCIALS_TAX_RATES}>{activity.data.display_name}</Link>.
        </>
      )
    case EventType.membership_created:
    case EventType.membership_updated:
    case EventType.membership_canceled:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
          {activity.data.package.name} membership for{' '}
          <Link to={ROUTES.CONTACT_DETAILS.replace(':id', activity.data.contact?.id)}>
            {formatFullName(activity.data.contact) || 'unknown contact'}
          </Link>
          .
        </>
      )
    case EventType.membership_bulk_updated:
    case EventType.membership_bulk_canceled:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
          <Link to={ROUTES.FINANCIALS_MEMBERSHIP_DETAIL.replace(':id', activity.data.id)}>
            {activity.data?.package?.name}
          </Link>{' '}
          membership for{' '}
          <Link to={ROUTES.ATTENDEE_DETAILS.replace(':id', activity.data?.program_attendee?.id)}>
            {formatFullName(activity.data.program_attendee) || 'unknown attendee'}
          </Link>
          .
        </>
      )
    case EventType.session_credit_created:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} added{' '}
          {activity.data?.credits?.length} credits to{' '}
          <Link to={ROUTES.CONTACT_CREDITS.replace(':id', activity.data?.contact?.id)}>
            {formatFullName(activity.data?.contact) || 'unknown contact'}
          </Link>
          's account.
        </>
      )
    case EventType.session_credit_updated:
    case EventType.session_credit_bulk_updated:
    case EventType.session_credit_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} a session credit on{' '}
          <Link to={ROUTES.CONTACT_CREDITS.replace(':id', activity.data.contact?.id)}>
            {formatFullName(activity.data?.contact) || 'unknown contact'}
          </Link>
          's account.
        </>
      )
    case EventType.membership_package_created:
    case EventType.membership_package_updated:
    case EventType.membership_package_discontinued:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} package{' '}
          <Link to={ROUTES.MEMBER_PACK_DETAILS.replace(':id', activity.data.id)}>
            {activity.data.name}
          </Link>
          .
        </>
      )
    case EventType.session_package_created:
    case EventType.session_package_updated:
    case EventType.session_package_discontinued:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} package{' '}
          <Link to={ROUTES.SESSION_PACK_DETAILS.replace(':id', activity.data.id)}>
            {activity.data.name}
          </Link>
          .
        </>
      )
    case EventType.membership_package_deleted:
    case EventType.session_package_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} package{' '}
          {activity.data.name}.
        </>
      )
    case EventType.note_created:
      if (!('parent_type' in activity.data)) {
        throw new Error('Note must have a type.')
      }
      switch (activity.data.parent_type) {
        case 'contact':
          return (
            <div>
              <div>
                {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
                <Link to={ROUTES.CONTACT_DETAILS.replace(':id', activity.data.parent || '')}>
                  note on {activity.data.parent_type}
                </Link>
                .
              </div>
              <div className="small text-body-secondary py-1">
                <NoteRow note={activity.data} hideDate={true} />
              </div>
            </div>
          )
        case 'attendee':
          return (
            <div>
              <div>
                {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
                <Link to={ROUTES.ATTENDEE_DETAILS.replace(':id', activity.data.parent || '')}>
                  note on {activity.data.parent_type}
                </Link>
                .
              </div>
              <div className="small text-body-secondary py-1">
                <NoteRow note={activity.data} hideDate={true} />
              </div>
            </div>
          )
        case 'order':
          return (
            <div>
              <div>
                {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
                <Link
                  to={ROUTES.FINANCIALS_ORDER_DETAIL.replace(':id', activity.data.parent || '')}
                >
                  note on {activity.data.parent_type}
                </Link>
                .
              </div>
              <div className="small text-body-secondary">
                <NoteRow note={activity.data} />
              </div>
            </div>
          )
        default:
          return (
            <>
              {activity.user ? activity.user.first_name : 'Citrus'} {action.replace('_', ' ')}{' '}
              {item.replace('_', ' ') + (activity.type.includes('bulk_') ? 's' : '')}.
            </>
          )
      }
    case EventType.refund_policy_updated:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action}{' '}
          <Link to={ROUTES.REFUND_POLICY_SETTINGS}>{item.replace('_', ' ')}</Link>.
        </>
      )
    case EventType.quick_filter_created:
    case EventType.quick_filter_updated:
    case EventType.quick_filter_deleted:
      return (
        <>
          {activity.user ? activity.user.first_name : 'Citrus'} {action} {item.replace('_', ' ')}{' '}
          <Link to={ROUTES.FILTER_SETTINGS}>{activity.data.name}</Link>.
        </>
      )
    default:
      return <DefaultActivityText activity={activity} />
  }
}
